import { create } from 'zustand'
import api from '../utils/api'

// ─── Sound generators (Web Audio API) ───────────────────────────────────────────
// Each returns { stop() } for cleanup.

function createSiren(ctx, gain) {
  // Classic siren: sine wave sweeping between 600–800 Hz via LFO
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = 700 // center

  const lfo = ctx.createOscillator()
  lfo.type = 'sine'
  lfo.frequency.value = 2 // 2 sweeps/sec

  const lfoGain = ctx.createGain()
  lfoGain.gain.value = 100 // ±100 Hz → 600–800

  lfo.connect(lfoGain)
  lfoGain.connect(osc.frequency)
  osc.connect(gain)
  osc.start()
  lfo.start()

  return { stop: () => { osc.stop(); lfo.stop() } }
}

function createBuzzer(ctx, gain) {
  // Harsh dissonant square waves with amplitude modulation
  const sub = ctx.createGain()
  sub.gain.value = 0.5

  const osc1 = ctx.createOscillator()
  osc1.type = 'square'
  osc1.frequency.value = 150

  const osc2 = ctx.createOscillator()
  osc2.type = 'square'
  osc2.frequency.value = 200

  osc1.connect(sub)
  osc2.connect(sub)
  sub.connect(gain)

  // Pulsing amplitude for urgency
  const ampLfo = ctx.createOscillator()
  ampLfo.type = 'sine'
  ampLfo.frequency.value = 8

  const ampDepth = ctx.createGain()
  ampDepth.gain.value = 0.3
  ampLfo.connect(ampDepth)
  ampDepth.connect(sub.gain)

  osc1.start()
  osc2.start()
  ampLfo.start()

  return { stop: () => { osc1.stop(); osc2.stop(); ampLfo.stop() } }
}

function createBell(ctx, gain) {
  // Repeating bell pings — triangle wave with exponential decay
  const oscs = []
  let alive = true

  function ping() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'triangle'
      osc.frequency.value = 800

      const env = ctx.createGain()
      env.gain.setValueAtTime(1, ctx.currentTime)
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)

      osc.connect(env)
      env.connect(gain)
      osc.start()
      osc.stop(ctx.currentTime + 0.5)
      oscs.push(osc)
    } catch { /* context closed */ }
  }

  ping()
  const interval = setInterval(ping, 600)

  return {
    stop: () => {
      alive = false
      clearInterval(interval)
      oscs.forEach((o) => { try { o.stop() } catch {} })
    },
  }
}

function createAirHorn(ctx, gain) {
  // Layered sawtooth waves with slight detuning for a fat foghorn
  const sub = ctx.createGain()
  sub.gain.value = 0.35

  const osc1 = ctx.createOscillator()
  osc1.type = 'sawtooth'
  osc1.frequency.value = 160

  const osc2 = ctx.createOscillator()
  osc2.type = 'sawtooth'
  osc2.frequency.value = 320.5 // detuned harmonic

  const osc3 = ctx.createOscillator()
  osc3.type = 'sawtooth'
  osc3.frequency.value = 480

  osc1.connect(sub)
  osc2.connect(sub)
  osc3.connect(sub)
  sub.connect(gain)

  osc1.start()
  osc2.start()
  osc3.start()

  return { stop: () => { osc1.stop(); osc2.stop(); osc3.stop() } }
}

function createPolice(ctx, gain) {
  // US police siren: alternating 2-tone wail (800Hz ↔ 1200Hz, ~1.5 cycles/sec)
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = 1000

  const lfo = ctx.createOscillator()
  lfo.type = 'square'
  lfo.frequency.value = 1.5

  const lfoGain = ctx.createGain()
  lfoGain.gain.value = 200

  lfo.connect(lfoGain)
  lfoGain.connect(osc.frequency)
  osc.connect(gain)
  osc.start()
  lfo.start()

  return { stop: () => { osc.stop(); lfo.stop() } }
}

function createFireAlarm(ctx, gain) {
  // Pulsing high-pitched tone (3100Hz on/off at 2Hz) — classic fire alarm T-3 pattern
  const osc = ctx.createOscillator()
  osc.type = 'square'
  osc.frequency.value = 3100

  const ampLfo = ctx.createOscillator()
  ampLfo.type = 'square'
  ampLfo.frequency.value = 2

  const ampGain = ctx.createGain()
  ampGain.gain.value = 1

  const env = ctx.createGain()
  env.gain.value = 0.6

  ampLfo.connect(ampGain)
  ampGain.connect(env.gain)
  osc.connect(env)
  env.connect(gain)

  osc.start()
  ampLfo.start()

  return { stop: () => { osc.stop(); ampLfo.stop() } }
}

function createNuclear(ctx, gain) {
  // Nuclear alert: slow descending wail (1400→400Hz over 3.5s, repeating)
  const oscs = []
  let alive = true

  function wail() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'sawtooth'
      osc.frequency.setValueAtTime(1400, ctx.currentTime)
      osc.frequency.linearRampToValueAtTime(400, ctx.currentTime + 3.5)

      const env = ctx.createGain()
      env.gain.setValueAtTime(0.5, ctx.currentTime)
      env.gain.setValueAtTime(0.5, ctx.currentTime + 3.0)
      env.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 3.5)

      osc.connect(env)
      env.connect(gain)
      osc.start()
      osc.stop(ctx.currentTime + 3.6)
      oscs.push(osc)
    } catch {}
  }

  wail()
  const interval = setInterval(wail, 4000)

  return {
    stop: () => {
      alive = false
      clearInterval(interval)
      oscs.forEach((o) => { try { o.stop() } catch {} })
    },
  }
}

function createAirRaid(ctx, gain) {
  // WW2 air raid siren: sine wave slowly sweeping 300→1200→300 Hz over 8s
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = 300

  let alive = true
  let rising = true

  function sweep() {
    if (!alive) return
    try {
      const now = ctx.currentTime
      if (rising) {
        osc.frequency.linearRampToValueAtTime(1200, now + 4)
      } else {
        osc.frequency.linearRampToValueAtTime(300, now + 4)
      }
      rising = !rising
    } catch {}
  }

  osc.connect(gain)
  osc.start()
  sweep()
  const interval = setInterval(sweep, 4000)

  return {
    stop: () => {
      alive = false
      clearInterval(interval)
      try { osc.stop() } catch {}
    },
  }
}

// ─── MODERATE sounds ─────────────────────────────────────────────────────────

function createEmergencyTone(ctx, gain) {
  // EAS-style two-tone alternating 853Hz and 960Hz
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = 906
  const lfo = ctx.createOscillator()
  lfo.type = 'square'
  lfo.frequency.value = 4
  const lfoGain = ctx.createGain()
  lfoGain.gain.value = 54
  lfo.connect(lfoGain)
  lfoGain.connect(osc.frequency)
  osc.connect(gain)
  osc.start(); lfo.start()
  return { stop: () => { osc.stop(); lfo.stop() } }
}

function createRadarPing(ctx, gain) {
  // Sonar blip: 1500Hz sine pulse every 0.8s
  const oscs = []
  let alive = true
  function ping() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = 1500
      const env = ctx.createGain()
      env.gain.setValueAtTime(0.8, ctx.currentTime)
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15)
      osc.connect(env); env.connect(gain)
      osc.start(); osc.stop(ctx.currentTime + 0.2)
      oscs.push(osc)
    } catch {}
  }
  ping()
  const interval = setInterval(ping, 800)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

function createAlarmClock(ctx, gain) {
  // Classic beep-beep-beep: 2000Hz square wave, 0.15s on / 0.15s off
  const osc = ctx.createOscillator()
  osc.type = 'square'
  osc.frequency.value = 2000
  const ampLfo = ctx.createOscillator()
  ampLfo.type = 'square'
  ampLfo.frequency.value = 3.33 // ~0.15s on, 0.15s off
  const ampGain = ctx.createGain()
  ampGain.gain.value = 1
  const env = ctx.createGain()
  env.gain.value = 0.5
  ampLfo.connect(ampGain); ampGain.connect(env.gain)
  osc.connect(env); env.connect(gain)
  osc.start(); ampLfo.start()
  return { stop: () => { osc.stop(); ampLfo.stop() } }
}

function createShipHorn(ctx, gain) {
  // Deep foghorn: 180Hz sawtooth with slow fade, 2s blast every 3s
  const oscs = []
  let alive = true
  function blast() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'sawtooth'
      osc.frequency.value = 180
      const osc2 = ctx.createOscillator()
      osc2.type = 'sawtooth'
      osc2.frequency.value = 181
      const sub = ctx.createGain()
      sub.gain.value = 0.4
      const env = ctx.createGain()
      env.gain.setValueAtTime(0.01, ctx.currentTime)
      env.gain.linearRampToValueAtTime(1, ctx.currentTime + 0.3)
      env.gain.setValueAtTime(1, ctx.currentTime + 1.5)
      env.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 2.2)
      osc.connect(sub); osc2.connect(sub); sub.connect(env); env.connect(gain)
      osc.start(); osc2.start()
      osc.stop(ctx.currentTime + 2.3); osc2.stop(ctx.currentTime + 2.3)
      oscs.push(osc, osc2)
    } catch {}
  }
  blast()
  const interval = setInterval(blast, 3000)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

// ─── GENTLE sounds ───────────────────────────────────────────────────────────

function createChime(ctx, gain) {
  // Ascending three-note melody (C5=523, E5=659, G5=784), sine, gentle decay
  const oscs = []
  let alive = true
  const notes = [523.25, 659.25, 783.99]
  function play() {
    if (!alive) return
    notes.forEach((freq, i) => {
      try {
        const t = ctx.currentTime + i * 0.25
        const osc = ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.value = freq
        const env = ctx.createGain()
        env.gain.setValueAtTime(0, t)
        env.gain.linearRampToValueAtTime(0.6, t + 0.05)
        env.gain.exponentialRampToValueAtTime(0.001, t + 0.8)
        osc.connect(env); env.connect(gain)
        osc.start(t); osc.stop(t + 0.9)
        oscs.push(osc)
      } catch {}
    })
  }
  play()
  const interval = setInterval(play, 2500)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

function createMarimba(ctx, gain) {
  // Warm wooden knock: 800Hz triangle with fast decay, repeating every 1s
  const oscs = []
  let alive = true
  function knock() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'triangle'
      osc.frequency.value = 800
      const osc2 = ctx.createOscillator()
      osc2.type = 'sine'
      osc2.frequency.value = 1600
      const sub = ctx.createGain()
      sub.gain.value = 0.3
      const env = ctx.createGain()
      env.gain.setValueAtTime(0.7, ctx.currentTime)
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
      osc.connect(env); osc2.connect(sub); sub.connect(env); env.connect(gain)
      osc.start(); osc2.start()
      osc.stop(ctx.currentTime + 0.35); osc2.stop(ctx.currentTime + 0.35)
      oscs.push(osc, osc2)
    } catch {}
  }
  knock()
  const interval = setInterval(knock, 1000)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

function createHarp(ctx, gain) {
  // Descending arpeggio (G5=784, E5=659, C5=523, G4=392), sine with gentle envelope
  const oscs = []
  let alive = true
  const notes = [783.99, 659.25, 523.25, 392.00]
  function play() {
    if (!alive) return
    notes.forEach((freq, i) => {
      try {
        const t = ctx.currentTime + i * 0.2
        const osc = ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.value = freq
        const env = ctx.createGain()
        env.gain.setValueAtTime(0, t)
        env.gain.linearRampToValueAtTime(0.5, t + 0.03)
        env.gain.exponentialRampToValueAtTime(0.001, t + 1.0)
        osc.connect(env); env.connect(gain)
        osc.start(t); osc.stop(t + 1.1)
        oscs.push(osc)
      } catch {}
    })
  }
  play()
  const interval = setInterval(play, 3000)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

function createSoftBell(ctx, gain) {
  // Single 1200Hz sine with long decay, repeating every 2s
  const oscs = []
  let alive = true
  function ring() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = 1200
      const env = ctx.createGain()
      env.gain.setValueAtTime(0.5, ctx.currentTime)
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5)
      osc.connect(env); env.connect(gain)
      osc.start(); osc.stop(ctx.currentTime + 1.6)
      oscs.push(osc)
    } catch {}
  }
  ring()
  const interval = setInterval(ring, 2000)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

function createPulse(ctx, gain) {
  // Subtle 400Hz pulse, smooth fade in/out, repeating every 1.5s
  const oscs = []
  let alive = true
  function beat() {
    if (!alive) return
    try {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = 400
      const env = ctx.createGain()
      env.gain.setValueAtTime(0.001, ctx.currentTime)
      env.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.3)
      env.gain.linearRampToValueAtTime(0.001, ctx.currentTime + 0.9)
      osc.connect(env); env.connect(gain)
      osc.start(); osc.stop(ctx.currentTime + 1.0)
      oscs.push(osc)
    } catch {}
  }
  beat()
  const interval = setInterval(beat, 1500)
  return { stop: () => { alive = false; clearInterval(interval); oscs.forEach(o => { try { o.stop() } catch {} }) } }
}

// ─── Module-level audio state (outside Zustand for non-serializable refs) ─────

let audioCtx = null
let gainNode = null
let activeSound = null

function stopAudio() {
  if (activeSound) {
    activeSound.stop()
    activeSound = null
  }
  if (audioCtx) {
    audioCtx.close().catch(() => {})
    audioCtx = null
    gainNode = null
  }
}

const CREATORS = {
  siren: createSiren,
  buzzer: createBuzzer,
  bell: createBell,
  airhorn: createAirHorn,
  police: createPolice,
  fire_alarm: createFireAlarm,
  nuclear: createNuclear,
  air_raid: createAirRaid,
  emergency_tone: createEmergencyTone,
  radar_ping: createRadarPing,
  alarm_clock: createAlarmClock,
  ship_horn: createShipHorn,
  chime: createChime,
  marimba: createMarimba,
  harp: createHarp,
  soft_bell: createSoftBell,
  pulse: createPulse,
}

function startAudio(sound, volume) {
  stopAudio()
  audioCtx = new (window.AudioContext || window.webkitAudioContext)()
  gainNode = audioCtx.createGain()
  gainNode.gain.value = volume
  gainNode.connect(audioCtx.destination)
  activeSound = (CREATORS[sound] || CREATORS.siren)(audioCtx, gainNode)
}

// ─── Dedup: track recently triggered alert IDs ──────────────────────────────────

const _recentTriggers = new Map()  // alertId → timestamp (or future timestamp for snooze)
const TRIGGER_DEDUP_MS = 300_000  // 5 minutes — once triggered, suppress re-triggers

function isDuplicate(alertId) {
  if (!alertId) return false
  const last = _recentTriggers.get(alertId)
  if (last && Date.now() - last < TRIGGER_DEDUP_MS) return true
  _recentTriggers.set(alertId, Date.now())
  // Prune old entries
  if (_recentTriggers.size > 100) {
    const now = Date.now()
    for (const [k, v] of _recentTriggers) {
      if (now - v > TRIGGER_DEDUP_MS) _recentTriggers.delete(k)
    }
  }
  return false
}

// ─── BroadcastChannel for cross-tab ack sync + single-tab leader election ────────

let _ackChannel = null
const _tabId = Math.random().toString(36).slice(2)  // unique per tab
let _isLeader = true  // assume leader until contested
let _leaderTimeout = null

try {
  _ackChannel = new BroadcastChannel('alrtz-ack')
  _ackChannel.onmessage = (e) => {
    if (e.data?.type === 'ACK' && e.data?.alertId) {
      const state = useAlarm.getState()
      // Clear from queue too
      for (let i = _alarmQueue.length - 1; i >= 0; i--) {
        if (_alarmQueue[i].alertId === e.data.alertId) _alarmQueue.splice(i, 1)
      }
      _recentTriggers.set(e.data.alertId, Date.now())
      if (state.isActive && state.alertData?.alertId === e.data.alertId) {
        stopAudio()
        useAlarm.setState({ isActive: false, alertData: null, queueCount: _alarmQueue.length })
        _showNextFromQueue()
      } else {
        useAlarm.setState({ queueCount: _alarmQueue.length })
      }
    } else if (e.data?.type === 'ALARM_CLAIM') {
      // Leader election: lowest tab ID wins
      if (e.data.tabId < _tabId) {
        _isLeader = false  // another tab has a lower ID — they're the leader
      }
    } else if (e.data?.type === 'LEADER_CLOSE') {
      // Leader tab closed — re-elect
      _isLeader = true  // optimistically claim leadership
      try { _ackChannel?.postMessage({ type: 'ALARM_CLAIM', tabId: _tabId }) } catch {}
    }
  }
  // Announce presence on load
  window.addEventListener('beforeunload', () => {
    try { _ackChannel?.postMessage({ type: 'LEADER_CLOSE', tabId: _tabId }) } catch {}
  })
} catch {}

// ─── Alarm queue (FIFO) ─────────────────────────────────────────────────────────

const _alarmQueue = []

function _showNextFromQueue() {
  if (_alarmQueue.length === 0) return
  const next = _alarmQueue.shift()
  const { volume, selectedSound, snoozedUntil } = useAlarm.getState()
  if (snoozedUntil && Date.now() < snoozedUntil) return
  startAudio(selectedSound, volume)
  useAlarm.setState({ isActive: true, alertData: next, queueCount: _alarmQueue.length })
}

// ─── Zustand store ──────────────────────────────────────────────────────────────

let _ackInFlight = false

const useAlarm = create((set, get) => ({
  isActive: false,
  alertData: null,
  queueCount: 0,
  volume: parseFloat(localStorage.getItem('alarm_volume') ?? '0.7'),
  selectedSound: localStorage.getItem('alarm_sound') ?? 'siren',
  snoozedUntil: null,

  trigger: (data) => {
    const { snoozedUntil, volume, selectedSound, isActive } = get()
    if (snoozedUntil && Date.now() < snoozedUntil) return
    if (isDuplicate(data?.alertId)) return

    // Don't queue if this alertId is already active or already in queue
    const state = get()
    if (state.isActive && state.alertData?.alertId === data?.alertId) return
    if (_alarmQueue.some(q => q.alertId === data?.alertId)) return

    // Leader election: broadcast claim, wait 50ms for contest
    _isLeader = true
    try { _ackChannel?.postMessage({ type: 'ALARM_CLAIM', tabId: _tabId }) } catch {}

    clearTimeout(_leaderTimeout)
    _leaderTimeout = setTimeout(() => {
      if (!_isLeader) return

      const s = get()
      if (s.isActive) {
        // Only queue if genuinely different alert
        if (!_alarmQueue.some(q => q.alertId === data?.alertId) && s.alertData?.alertId !== data?.alertId) {
          _alarmQueue.push(data)
          set({ queueCount: _alarmQueue.length })
        }
        return
      }

      startAudio(selectedSound, volume)
      set({ isActive: true, alertData: data, queueCount: _alarmQueue.length })
    }, 60)
  },

  acknowledge: () => {
    const { alertData, isActive } = get()
    if (!isActive && !alertData) return
    stopAudio()

    // Remove ALL duplicates of this alert from the queue (same alertId OR same coin)
    const ackId = alertData?.alertId
    const ackCoin = alertData?.coin
    const before = _alarmQueue.length
    for (let i = _alarmQueue.length - 1; i >= 0; i--) {
      const q = _alarmQueue[i]
      if (q.alertId === ackId || (ackCoin && q.coin === ackCoin)) {
        _alarmQueue.splice(i, 1)
      }
    }

    // Also add to dedup so it won't re-trigger from SSE
    if (ackId) _recentTriggers.set(ackId, Date.now())

    set({ isActive: false, alertData: null, queueCount: _alarmQueue.length })

    // Broadcast ack to other tabs / extension
    if (ackId) {
      try { _ackChannel?.postMessage({ type: 'ACK', alertId: ackId }) } catch {}
    }
    // Pause the alert via API
    if (ackId && !_ackInFlight) {
      _ackInFlight = true
      api.patch(`/alerts/${ackId}`, { is_active: false })
        .catch(() => {})
        .finally(() => { _ackInFlight = false })
    }
    // Show next DIFFERENT queued alarm after short delay
    setTimeout(_showNextFromQueue, 300)
  },

  snooze: (minutes = 5) => {
    const { alertData } = get()
    stopAudio()
    // Add current alert to dedup so it won't re-trigger during snooze
    if (alertData?.alertId) _recentTriggers.set(alertData.alertId, Date.now() + minutes * 60 * 1000)
    _alarmQueue.length = 0
    set({
      isActive: false,
      alertData: null,
      queueCount: 0,
      snoozedUntil: Date.now() + minutes * 60 * 1000,
    })
  },

  setVolume: (vol) => {
    localStorage.setItem('alarm_volume', String(vol))
    if (gainNode) gainNode.gain.value = vol
    set({ volume: vol })
  },

  setSelectedSound: (sound) => {
    localStorage.setItem('alarm_sound', sound)
    const { isActive, volume } = get()
    if (isActive) {
      startAudio(sound, volume)
    }
    set({ selectedSound: sound })
  },
}))

// Flat map for lookups (key → label)
export const ALARM_SOUNDS = {
  siren: 'Siren', buzzer: 'Buzzer', bell: 'Bell', airhorn: 'Air Horn',
  police: 'Police Siren', fire_alarm: 'Fire Alarm', nuclear: 'Nuclear Alert',
  air_raid: 'Air Raid', emergency_tone: 'Emergency Tone',
  radar_ping: 'Radar Ping', alarm_clock: 'Alarm Clock', ship_horn: 'Ship Horn',
  chime: 'Chime', marimba: 'Marimba', harp: 'Harp', soft_bell: 'Soft Bell', pulse: 'Pulse',
}

// Categorized for Settings UI
export const ALARM_SOUND_GROUPS = [
  { label: 'Intense', emoji: '\uD83D\uDD34', keys: ['siren', 'buzzer', 'airhorn', 'police', 'fire_alarm', 'nuclear', 'air_raid', 'emergency_tone'] },
  { label: 'Moderate', emoji: '\uD83D\uDFE1', keys: ['bell', 'radar_ping', 'alarm_clock', 'ship_horn'] },
  { label: 'Gentle', emoji: '\uD83D\uDFE2', keys: ['chime', 'marimba', 'harp', 'soft_bell', 'pulse'] },
]

// Preview a sound for 2 seconds then stop
export function previewSound(soundKey, volume = 0.7) {
  stopAudio()
  startAudio(soundKey, volume)
  setTimeout(stopAudio, 2000)
}

export default useAlarm
